import { useEffect, useState } from 'react';
import { useAddressLookup, isValidPostcode } from '../../hooks/useAddressLookup';
import type { AddressFields as Fields } from '../../stores/offerte-panel-store';

interface Props {
  label: string;
  value: Fields;
  onChange: (fields: Partial<Fields>) => void;
}

function formatPostcode(raw: string): string {
  const clean = raw.replace(/\s/g, '').toUpperCase();
  if (clean.length >= 5 && /^\d{4}[A-Z]/.test(clean)) {
    return `${clean.slice(0, 4)} ${clean.slice(4, 6)}`;
  }
  return clean;
}

export function AddressFieldsForm({ label, value, onChange }: Props) {
  const { lookup, result, isLoading, isManualMode, error } = useAddressLookup();
  const [forceManual, setForceManual] = useState(false);

  // Trigger lookup when postcode + huisnummer are both filled
  useEffect(() => {
    if (value.postalCode && value.houseNumber) {
      lookup(value.postalCode, value.houseNumber);
    }
  }, [value.postalCode, value.houseNumber, lookup]);

  // Apply lookup result to store — only fill if empty or we just looked up new values
  useEffect(() => {
    if (!result) return;
    onChange({ street: result.street, city: result.city });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  const postcodeValid = isValidPostcode(value.postalCode);
  const haveKey = value.postalCode && value.houseNumber;
  const showManualFields = !!haveKey && (isManualMode || forceManual || (!isLoading && !result));

  return (
    <div className="space-y-2">
      <label className="text-xs font-medium text-slate-600 block">{label}</label>

      <div className="grid grid-cols-6 gap-2">
        <input
          type="text"
          placeholder="Postcode"
          value={value.postalCode}
          onChange={(e) => onChange({ postalCode: formatPostcode(e.target.value) })}
          autoComplete="postal-code"
          className="col-span-2 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <input
          type="text"
          placeholder="Huisnr"
          value={value.houseNumber}
          onChange={(e) => onChange({ houseNumber: e.target.value })}
          autoComplete="address-line2"
          className="col-span-2 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <input
          type="text"
          placeholder="Toev."
          value={value.houseNumberAddition}
          onChange={(e) => onChange({ houseNumberAddition: e.target.value })}
          className="col-span-2 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {postcodeValid && value.houseNumber && isLoading && (
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <div className="w-3 h-3 border-2 border-slate-200 border-t-indigo-600 rounded-full animate-spin" />
          Adres zoeken...
        </div>
      )}

      {result && !forceManual && !isManualMode && (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-600">
            {result.street}, {result.city}
          </span>
          <button
            type="button"
            onClick={() => setForceManual(true)}
            className="text-indigo-600 hover:underline"
          >
            Klopt niet?
          </button>
        </div>
      )}

      {error && !result && (
        <p className="text-xs text-amber-600">{error} — vul handmatig in</p>
      )}

      {showManualFields && (
        <div className="grid grid-cols-2 gap-2">
          <input
            type="text"
            placeholder="Straat"
            value={value.street}
            onChange={(e) => onChange({ street: e.target.value })}
            autoComplete="address-line1"
            className="px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <input
            type="text"
            placeholder="Plaats"
            value={value.city}
            onChange={(e) => onChange({ city: e.target.value })}
            autoComplete="address-level2"
            className="px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      )}
    </div>
  );
}
